import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  clearPostDecisionQueries,
  DecisionOutcome,
  runDecisionPolling,
} from '../utils/decisionPolling';

export type DecisionPollingStatus =
  | 'idle'
  | 'submitting'
  | 'approved'
  | 'partial'
  | 'declined'
  | 'blocked'
  | 'timeout'
  | 'error';

export function useDecisionPolling() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DecisionPollingStatus>('idle');
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const isRunningRef = useRef(false);

  const start = useCallback(
    async ({
      stateKey,
      payLinkAmount,
    }: {
      stateKey: string;
      payLinkAmount?: number;
    }): Promise<DecisionPollingStatus> => {
      if (isRunningRef.current) return 'submitting';
      isRunningRef.current = true;
      setStatus('submitting');

      let outcome: DecisionOutcome | null;
      try {
        outcome = await runDecisionPolling({
          stateKey,
          payLinkAmount,
          isCancelled: () => cancelledRef.current,
        });
      } catch {
        if (!cancelledRef.current) setStatus('error');
        return 'error';
      } finally {
        isRunningRef.current = false;
      }

      if (cancelledRef.current || outcome === null) return 'idle';

      clearPostDecisionQueries(queryClient);

      setStatus(outcome);
      return outcome;
    },
    [queryClient],
  );

  return { status, start };
}
